import React, { useEffect, useState, useCallback } from 'react';
import { School, BookOpen, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAcademicYear } from '../../context/AcademicYearContext';
import { useT } from '../../i18n/LanguageContext';
import { schoolApi } from '../../api/school';
import { PageHeader, EmptyState, LoadingState, Card, StatCard } from '../../components/school/ui';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

export default function TeacherPortalPage() {
  const { user } = useAuth();
  const { selectedYearId } = useAcademicYear();
  const { t } = useT();
  const navigate = useNavigate();
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noProfile, setNoProfile] = useState(false);

  const init = useCallback(async () => {
    try { setLoading(true); setNoProfile(false); setTeacher(await schoolApi.myTeacher(selectedYearId)); }
    catch (e) { if (e?.response?.status === 404) setNoProfile(true); else toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [selectedYearId]);

  useEffect(() => { init(); }, [init]);

  if (loading) return <div className="max-w-[1400px] mx-auto"><LoadingState rows={6} /></div>;
  if (noProfile) return <div className="max-w-[1400px] mx-auto"><Card><EmptyState icon={School} title={t('portal.noProfile')} /></Card></div>;

  const subjects = teacher?.subjects || [];
  const sections = teacher?.assigned_sections || [];

  return (
    <div className="max-w-[1400px] mx-auto">
      <PageHeader title={t('portal.teacherTitle')} subtitle={teacher?.name} icon={School}
        actions={<Button onClick={() => navigate('/school/marks')} className="gap-2" data-testid="teacher-goto-marks"><BookOpen className="w-4 h-4" /> {t('nav.marks')}</Button>} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <StatCard label={t('teachers.subjects')} value={teacher?.subject_count ?? subjects.length} icon={BookOpen} accent="exams" data-testid="teacher-stat-subjects" />
        <StatCard label={t('classes.title')} value={teacher?.classes_sections_count ?? sections.length} icon={School} accent="classes" data-testid="teacher-stat-classes" />
        <StatCard label={t('dash.students')} value={teacher?.student_count ?? 0} icon={Users} accent="students" data-testid="teacher-stat-students" />
      </div>

      <Card className="mb-4">
        <div className="px-5 py-4 border-b border-border"><h3 className="text-sm font-semibold">{t('teachers.subjects')}</h3></div>
        {subjects.length === 0 ? <EmptyState icon={BookOpen} title={t('common.noData')} /> : (
          <div className="divide-y divide-border">
            {subjects.map((s) => (
              <div key={`${s.id}-${s.section_id || 'all'}`} className="px-5 py-3 flex items-center justify-between" data-testid={`teacher-subject-${s.id}`}>
                <div className="flex items-center gap-3"><BookOpen className="w-4 h-4 text-muted-foreground" /><span className="font-medium text-sm">{s.name}</span>{s.code && <Badge variant="outline" className="font-mono">{s.code}</Badge>}</div>
                <span className="text-sm text-muted-foreground">{s.class_name}{s.section_name ? ` / ${s.section_name}` : ''}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {sections.length > 0 && (
        <Card>
          <div className="px-5 py-4 border-b border-border"><h3 className="text-sm font-semibold">{t('classes.title')}</h3></div>
          <div className="divide-y divide-border">
            {sections.map((s) => (
              <div key={s.id} className="px-5 py-3 flex items-center justify-between" data-testid={`teacher-section-${s.id}`}>
                <span className="font-medium text-sm">{s.class_name} / {s.name}</span>
                <Badge variant="outline">{s.student_count} {t('dash.students')}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
